import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Polygon, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { MapPin, Square, Trash2, Save, Edit } from "lucide-react";

// Fix for default markers in React Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface StockAreaGeoData {
  id: number;
  name: string;
  areaCode: string;
  areaType: string;
  geoFence: GeoJSON.Polygon;
  centerLatitude?: number;
  centerLongitude?: number;
  status: string;
  maxCapacity?: number;
  capacityUnit: string;
}

interface GeoFenceMapProps {
  stockAreas?: StockAreaGeoData[];
  selectedAreaId?: number;
  onAreaSelect?: (areaId: number) => void;
  onGeoFenceChange?: (geoFence: GeoJSON.Polygon, center: { lat: number; lng: number }) => void;
  editMode?: boolean;
  initialGeoFence?: GeoJSON.Polygon;
  initialCenter?: { lat: number; lng: number };
  height?: string;
}

// Drawing controls component
function DrawingControls({ 
  isDrawing, 
  onStartDrawing, 
  onClearDrawing, 
  onSaveDrawing, 
  hasDrawing 
}: {
  isDrawing: boolean;
  onStartDrawing: () => void;
  onClearDrawing: () => void;
  onSaveDrawing: () => void;
  hasDrawing: boolean;
}) {
  return (
    <Card className="absolute top-4 right-4 z-[1000] w-56">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Square className="h-4 w-4" />
          Drawing Tools
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {!isDrawing && !hasDrawing && (
          <Button 
            onClick={onStartDrawing}
            className="w-full"
            data-testid="button-start-drawing"
          >
            <Square className="h-4 w-4 mr-2" />
            Draw Area
          </Button>
        )}
        
        {isDrawing && (
          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">
              Click to add points, double-click to finish
            </div>
            <Button 
              variant="outline" 
              onClick={onClearDrawing}
              className="w-full"
              data-testid="button-cancel-drawing"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        )}
        
        {hasDrawing && !isDrawing && (
          <div className="space-y-2">
            <Button 
              onClick={onSaveDrawing}
              className="w-full"
              data-testid="button-save-drawing"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Area
            </Button>
            <Button 
              variant="outline" 
              onClick={onClearDrawing}
              className="w-full"
              data-testid="button-clear-drawing"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear
            </Button>
            <Button 
              variant="outline" 
              onClick={onStartDrawing}
              className="w-full"
              data-testid="button-redraw"
            >
              <Edit className="h-4 w-4 mr-2" />
              Redraw
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Map component with drawing functionality
function DrawingMap({ 
  onGeoFenceChange, 
  editMode, 
  initialGeoFence,
  initialCenter,
  isDrawing, 
  setIsDrawing,
  hasDrawing,
  setHasDrawing,
  currentPolygon,
  setCurrentPolygon
}: {
  onGeoFenceChange?: (geoFence: GeoJSON.Polygon, center: { lat: number; lng: number }) => void;
  editMode?: boolean;
  initialGeoFence?: GeoJSON.Polygon;
  initialCenter?: { lat: number; lng: number };
  isDrawing: boolean;
  setIsDrawing: (drawing: boolean) => void;
  hasDrawing: boolean;
  setHasDrawing: (has: boolean) => void;
  currentPolygon: [number, number][];
  setCurrentPolygon: (polygon: [number, number][]) => void;
}) {
  const map = useMap();
  
  useMapEvents({
    click(e) {
      if (isDrawing) {
        const newPoint: [number, number] = [e.latlng.lat, e.latlng.lng];
        setCurrentPolygon([...currentPolygon, newPoint]);
      }
    },
    dblclick(e) {
      if (isDrawing && currentPolygon.length >= 3) {
        setIsDrawing(false);
        setHasDrawing(true);
        
        // Create GeoJSON polygon
        const geoFence: GeoJSON.Polygon = {
          type: "Polygon",
          coordinates: [[...currentPolygon.map(p => [p[1], p[0]]), [currentPolygon[0][1], currentPolygon[0][0]]]]
        };
        
        // Calculate center point
        const center = {
          lat: currentPolygon.reduce((sum, p) => sum + p[0], 0) / currentPolygon.length,
          lng: currentPolygon.reduce((sum, p) => sum + p[1], 0) / currentPolygon.length
        };
        
        onGeoFenceChange?.(geoFence, center);
      }
    }
  });

  // Load initial geo fence if provided
  useEffect(() => {
    if (editMode && initialGeoFence && initialCenter) {
      try {
        const coordinates = initialGeoFence.coordinates[0];
        const polygon: [number, number][] = coordinates.slice(0, -1).map(coord => [coord[1], coord[0]]);
        setCurrentPolygon(polygon);
        setHasDrawing(true);
        map.setView([initialCenter.lat, initialCenter.lng], map.getZoom());
      } catch (error) {
        console.error('Error loading initial geo fence:', error);
      }
    }
  }, [editMode, initialGeoFence, initialCenter, map, setCurrentPolygon, setHasDrawing]);

  return null;
}

// Area status color mapping
const getAreaStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'active': return '#22c55e'; // green
    case 'inactive': return '#6b7280'; // gray
    case 'maintenance': return '#f59e0b'; // amber
    case 'full': return '#ef4444'; // red
    default: return '#3b82f6'; // blue
  }
};

const getAreaTypeIcon = (areaType: string) => {
  switch (areaType?.toLowerCase()) {
    case 'storage': return '📦';
    case 'staging': return '🚛';
    case 'loading': return '🏗️';
    case 'restricted': return '🚫';
    default: return '📍';
  }
};

export default function GeoFenceMap({
  stockAreas = [],
  selectedAreaId,
  onAreaSelect,
  onGeoFenceChange,
  editMode = false,
  initialGeoFence,
  initialCenter,
  height = "500px"
}: GeoFenceMapProps) {
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawing, setHasDrawing] = useState(false);
  const [currentPolygon, setCurrentPolygon] = useState<[number, number][]>([]);

  // Default map center (you can adjust this based on your location)
  const defaultCenter: [number, number] = [-29.8587, 31.0218]; // Durban Port, South Africa
  const mapCenter = initialCenter ? [initialCenter.lat, initialCenter.lng] as [number, number] : defaultCenter;

  const handleStartDrawing = () => {
    setCurrentPolygon([]);
    setIsDrawing(true);
    setHasDrawing(false);
  };

  const handleClearDrawing = () => {
    setCurrentPolygon([]);
    setIsDrawing(false);
    setHasDrawing(false);
    onGeoFenceChange?.(undefined as any, undefined as any);
  };

  const handleSaveDrawing = () => {
    if (currentPolygon.length >= 3) {
      const geoFence: GeoJSON.Polygon = {
        type: "Polygon",
        coordinates: [[...currentPolygon.map(p => [p[1], p[0]]), [currentPolygon[0][1], currentPolygon[0][0]]]]
      };
      
      const center = {
        lat: currentPolygon.reduce((sum, p) => sum + p[0], 0) / currentPolygon.length,
        lng: currentPolygon.reduce((sum, p) => sum + p[1], 0) / currentPolygon.length
      };
      
      onGeoFenceChange?.(geoFence, center);
    }
  };

  const convertGeoJsonToLeafletPolygon = (geoJson: GeoJSON.Polygon): [number, number][] => {
    try {
      return geoJson.coordinates[0].slice(0, -1).map(coord => [coord[1], coord[0]]);
    } catch (error) {
      console.error('Error converting GeoJSON to Leaflet polygon:', error);
      return [];
    }
  };

  return (
    <div className="relative">
      <MapContainer
        center={mapCenter}
        zoom={13}
        style={{ height, width: '100%' }}
        className="rounded-lg border"
        data-testid="geo-fence-map"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {/* Drawing functionality */}
        <DrawingMap 
          onGeoFenceChange={onGeoFenceChange}
          editMode={editMode}
          initialGeoFence={initialGeoFence}
          initialCenter={initialCenter}
          isDrawing={isDrawing}
          setIsDrawing={setIsDrawing}
          hasDrawing={hasDrawing}
          setHasDrawing={setHasDrawing}
          currentPolygon={currentPolygon}
          setCurrentPolygon={setCurrentPolygon}
        />
        
        {/* Current drawing polygon */}
        {(isDrawing || hasDrawing) && currentPolygon.length >= 3 && (
          <Polygon
            positions={currentPolygon}
            pathOptions={{
              color: isDrawing ? '#ef4444' : '#22c55e',
              fillColor: isDrawing ? '#ef4444' : '#22c55e',
              fillOpacity: 0.2,
              weight: 2
            }}
          />
        )}
        
        {/* Drawing points */}
        {isDrawing && currentPolygon.map((point, index) => (
          <Marker 
            key={index} 
            position={point}
            icon={L.divIcon({
              className: 'draw-point',
              html: `<div style="background: #ef4444; width: 8px; height: 8px; border-radius: 50%; border: 2px solid white;"></div>`,
              iconSize: [12, 12],
              iconAnchor: [6, 6]
            })}
          />
        ))}
        
        {/* Existing stock areas */}
        {stockAreas.map((area) => {
          try {
            const polygonCoords = convertGeoJsonToLeafletPolygon(area.geoFence);
            const isSelected = selectedAreaId === area.id;
            const color = isSelected ? '#3b82f6' : getAreaStatusColor(area.status);
            
            return (
              <div key={area.id}>
                <Polygon
                  positions={polygonCoords}
                  pathOptions={{
                    color,
                    fillColor: color,
                    fillOpacity: isSelected ? 0.3 : 0.2,
                    weight: isSelected ? 3 : 2
                  }}
                  eventHandlers={{
                    click: () => onAreaSelect?.(area.id)
                  }}
                  data-testid={`area-polygon-${area.id}`}
                />
                
                {/* Area center marker */}
                {area.centerLatitude && area.centerLongitude && (
                  <Marker 
                    position={[area.centerLatitude, area.centerLongitude]}
                    eventHandlers={{
                      click: () => onAreaSelect?.(area.id)
                    }}
                    data-testid={`area-marker-${area.id}`}
                  >
                    <Popup>
                      <div className="p-2 min-w-[200px]">
                        <div className="font-semibold text-base mb-2">
                          {getAreaTypeIcon(area.areaType)} {area.name}
                        </div>
                        <div className="space-y-1 text-sm">
                          <div><strong>Code:</strong> {area.areaCode}</div>
                          <div><strong>Type:</strong> {area.areaType}</div>
                          <div className="flex items-center gap-2">
                            <strong>Status:</strong>
                            <Badge 
                              variant="outline" 
                              style={{ 
                                borderColor: color,
                                color: color
                              }}
                            >
                              {area.status}
                            </Badge>
                          </div>
                          {area.maxCapacity && (
                            <div><strong>Capacity:</strong> {area.maxCapacity} {area.capacityUnit}</div>
                          )}
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                )}
              </div>
            );
          } catch (error) {
            console.error(`Error rendering area ${area.id}:`, error);
            return null;
          }
        })}
      </MapContainer>
      
      {/* Drawing controls */}
      {editMode && (
        <DrawingControls
          isDrawing={isDrawing}
          onStartDrawing={handleStartDrawing}
          onClearDrawing={handleClearDrawing}
          onSaveDrawing={handleSaveDrawing}
          hasDrawing={hasDrawing}
        />
      )}
      
      {/* Area info panel */}
      {stockAreas.length > 0 && !editMode && (
        <Card className="absolute bottom-4 left-4 z-[1000] w-64">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              Stock Areas ({stockAreas.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {stockAreas.map((area) => (
                <div 
                  key={area.id}
                  className={`p-2 rounded cursor-pointer transition-colors ${
                    selectedAreaId === area.id 
                      ? 'bg-primary/10 border border-primary' 
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                  onClick={() => onAreaSelect?.(area.id)}
                  data-testid={`area-info-${area.id}`}
                >
                  <div className="font-medium text-sm">
                    {getAreaTypeIcon(area.areaType)} {area.name}
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{area.areaCode}</span>
                    <Badge 
                      variant="outline" 
                      className="text-xs"
                      style={{ 
                        borderColor: getAreaStatusColor(area.status),
                        color: getAreaStatusColor(area.status)
                      }}
                    >
                      {area.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}